import * as ElasticAppSearch from "@elastic/app-search-javascript";
import Axios from "../../base/ts/composables/Axios";
import { useGA4 } from "../../base/ts/composables/GA4";
import { debounce } from "lodash-es";

const { trackGAViewItemList } = useGA4();

export default class QuickSearch {
    constructor() {
        this.xhr = false;
        this.resultHtml = '<a href="{0}"><span class="img" style="{1}"></span><span class="title">{2}</span></a>';
        this.noresultsHtml = '<div class="filler"></div>';
        this.searchClients = {
            product: null,
            pages: null,
        };

        // Currently only used for GA tracking
        this.products = [];
        this.trackGADebounced = debounce(this.trackGA.bind(this), 1000);

        this.elasticAppHost = process.env.MIX_ELASTICAPPSEARCH_HOST;
        this.elasticAppToken = process.env.MIX_ELASTICAPPSEARCH_TOKEN;

        if (this.elasticAppHost && this.elasticAppToken) {
            this.createClient("product");
            this.createClient("pages");
        }

        this.initListeners();
    }

    initListeners() {
        this.$productResults = document.querySelector("#quick_results_products .result_set");
        this.$pageResults = document.querySelector("#quick_results_pages .result_set");

        // If the required elements are not (yet) present on the page, retry in n milliseconds.
        if (!this.$productResults || !this.$pageResults) {
            setTimeout(this.initListeners.bind(this), 200);
            return;
        }

        if (document.querySelector("#searchcontainer input")) {
            document.querySelector("#searchcontainer input").addEventListener("keyup", (event) => {
                this.search.call(this, event);
            });
        }

        if (document.querySelector("#searchcontainer input")) {
            document.querySelector("#searchcontainer input").addEventListener("focusin", (event) => {
                this.focusin.call(this, event);
            });
        }

        document.addEventListener("click", (event) => {
            let isClosest = event.target.closest("#quick_results");
            if (!isClosest) {
                isClosest = event.target.closest("#searchcontainer");
            }
            if (!isClosest && document.querySelector("body").classList.contains("search_focus")) {
                this.close.call(this, event);
            }
        });

        if (document.querySelector("#quick_results .close")) {
            document.querySelector("#quick_results .close").addEventListener("click", (event) => {
                this.close.call(this, event);
            });
        }

        this.previousRequests = [];
        this.allCategories = null;
        this.fetchAllCategories();
    }

    search(event) {
        this.trackGADebounced.cancel();

        if (this.previousRequests.length > 0) {
            this.previousRequests.forEach((request) => (request.cancel ? request.cancel() : request.abort()));
            this.previousRequests = [];
        }

        let currentPhrase = event.target.value;

        if (currentPhrase === "") {
            this.searchCategories(currentPhrase);
        } else {
            this.searchProducts(currentPhrase);
            this.searchCategories(currentPhrase);
        }
    }

    searchProducts(name) {
        if (this.searchClients.product) {
            const options = {};
            options.page = {
                size: 8,
            };

            options.filters = {
                store_ids: [window.store_id.toString()],
            };

            let query = name.split(" ").join(" OR ");
            this.elasticAppSearchRequest("product", query, options, (response) => this.setProducts(response.results));
        } else {
            let query = {
                "search[name]": `*${name}*`,
                "search[sku]": `*${name}*`,
                includes: "media.versions",
                per_page: 8,
                ...(window.store_id && { "filter[store_ids]": window.store_id }),
            };

            this.apiSearchRequest(window.ajax_object.daalder_api_url + "v2/products", query, (responseData) =>
                this.setProducts(responseData.data.products),
            );
        }
    }

    searchCategories(name) {
        if (this.searchClients.pages) {
            let query = name.split(" ").join(" OR ");

            this.elasticAppSearchRequest(
                "pages",
                query,
                {
                    page: { size: 4 },
                    filters: {
                        post_type: ["page"],
                    },
                },
                (response) => this.setCategories(response.results),
            );
        } else {
            if (!this.allCategories) {
                this.fetchAllCategories(name);
                return;
            }

            if (!name.length) {
                this.setCategories(this.allCategories);
                return;
            }

            name = name.toLowerCase();
            let results = [];

            for (let i = 0; i < this.allCategories.length; i++) {
                if (this.allCategories[i].name.toLowerCase().includes(name)) {
                    results.push(this.allCategories[i]);
                }

                if (results.length > 3) {
                    break;
                }
            }

            this.setCategories(results);
        }
    }

    fetchAllCategories(name) {
        Axios.getInstance()
            .get("/app/cache/categories-index.json")
            .then((result) => {
                this.allCategories = result.data.categories;
                if (name) {
                    this.searchCategories(name);
                }
            });
    }

    apiSearchRequest(url, query, callback) {
        let request = Axios.getInstance().CancelToken.source();
        this.previousRequests.push(request);

        Axios.getInstance()
            .get(url, { params: query, cancelToken: request.token })
            .then((response) => {
                this.clearRequest(request);
                callback(response.data);
            })
            .catch(function (thrown) {
                if (Axios.getInstance().isCancel(thrown)) {
                    return; // pass, it's ok
                }
                throw thrown;
            });
    }

    createClient(engineName) {
        this.searchClients[engineName] = ElasticAppSearch.createClient({
            searchKey: this.elasticAppToken,
            endpointBase: this.elasticAppHost,
            engineName: engineName,
        });

        this.elasticAppSearchRequest(engineName, "", { page: { size: 1 } }, (results) =>
            this.checkAppSearchClient(engineName, results),
        );
    }

    checkAppSearchClient(engineName, response) {
        if (!response.results || !response.results.length) {
            this.searchClients[engineName] = null;
        }
    }

    elasticAppSearchRequest(engineName, query, options, callback) {
        this.searchClients[engineName]
            .search(query, options)
            .then((response) => callback(response))
            .catch((error) => {});
    }

    clearRequest(request) {
        let requestIndex = this.previousRequests.indexOf(request);
        if (requestIndex > -1) {
            this.previousRequests.splice(requestIndex, 1);
        }
    }

    setProducts(products) {
        this.products = products;

        products = products.map((rawProduct) => {
            let source = {
                type: "Simple",
                image: this.searchClients.product ? rawProduct.getRaw("image") : rawProduct.media?.[0]?.src,
                ...(this.searchClients.product && {
                    name: rawProduct.getRaw("name"),
                    url: rawProduct.getRaw("url"),
                }),
            };

            return Object.assign(rawProduct, source);
        });

        this.setResults(8, products, this.$productResults);

        this.trackGADebounced();
    }

    setCategories(categories) {
        if (this.searchClients.pages) {
            categories = categories.map((category) => {
                let source = {
                    type: "Simple",
                    image: category.getRaw("image"),
                    name: category.getRaw("wp_title") ?? category.getRaw("title"),
                    url: category.getRaw("url_path").replace(/^\//g, ""),
                };

                return Object.assign(category, source);
            });
        }

        this.setResults(4, categories, this.$pageResults);
    }

    setResults(fillerCount, resultSet, $resultContainer) {
        $resultContainer.innerHTML = "";

        let htmlToAdd = "";

        if (resultSet.length > 0) {
            for (let result of resultSet) {
                let style = "";
                if (result.image && Object.keys(result.image).length !== 0 && result.image !== undefined) {
                    style = "background-image:url(" + result.image + ")";
                }

                htmlToAdd += this.resultHtml.format("/" + result.url, style, result.name);
            }
        }

        if (fillerCount - resultSet.length > 0) {
            for (let i = 0; i < fillerCount - resultSet.length; i++) {
                htmlToAdd += this.noresultsHtml;
            }
        }

        $resultContainer.innerHTML += htmlToAdd;
    }

    focusin() {
        document.querySelectorAll("#quick_results span.img").forEach((element) => {
            if (element.dataset && element.dataset.image) {
                element.style.backgroundImage = "url(" + element.dataset.image + ")";
            }
        });

        document.querySelector("body").classList.add("search_focus");
    }

    close(event) {
        document.querySelector("body").classList.remove("search_focus");
    }

    trackGA() {
        if (this.products?.length > 0) {
            trackGAViewItemList(this.products, "quicksearch");
        }
    }
}
